import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/tabs';// hazardous-chemicals/components/ChemicalRegister.tsx
import React, { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Search, Upload, Download, Plus, FlaskConical, TriangleAlert, Eye, Pencil, Loader2, AlertTriangle, Archive, RefreshCw } from 'lucide-react';
import { GhsPill, DgTag, NoSdsTag, ReviewDueTag, isReviewDue, sdsStatusOf } from '../utils/safety-utils';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';
import { SdsUploadDialog } from './SdsUploadDialog';
import type { Chemical } from '../../../../types/chemical.types';

const COLS = ['Chemical Name', 'Quantity', 'GHS Signal', 'UN Number', 'Poison / pH', 'Storage', 'Intended Use', 'PPE Required', 'SDS', 'Location', 'Owner', 'Actions'];

export const ChemicalRegister: React.FC<{
    chemicals: Chemical[];
    search: string;
    onSearch: (s: string) => void;
    searchColumn: string;
    onSearchColumn: (col: string) => void;
    activeTab: 'active' | 'archived';
    onTabChange: (tab: 'active' | 'archived') => void;
    onAdd?: () => void;
    onUploadSds: (chemicalId: string, file: File) => Promise<void>;
    onExport: (selected: Chemical[]) => void;
    onEdit?: (c: Chemical) => void;
    onArchive?: (id: string) => void;
    onRestore?: (id: string) => void;
}> = ({ chemicals, search, onSearch, searchColumn, onSearchColumn, activeTab, onTabChange, onAdd, onUploadSds, onExport, onEdit, onArchive, onRestore }) => {
    const navigate = useNavigate();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [chemicalToArchive, setChemicalToArchive] = useState<Chemical | null>(null);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [sdsDialogOpen, setSdsDialogOpen] = useState(false);
    const missingSds = chemicals.filter((c) => !c.deleted && !c.sds).length;
    const expiredReviews = chemicals.filter((c) => !c.deleted && isReviewDue(c.lastReview)).length;

    const allSelected = chemicals.length > 0 && selectedIds.size === chemicals.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < chemicals.length;

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(chemicals.map((c) => c.id)));
        }
    };

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleExport = () => {
        const selected = chemicals.filter((c) => selectedIds.has(c.id));
        // If none selected, export all visible chemicals
        onExport(selected.length > 0 ? selected : chemicals);
    };

    const openArchiveModal = (chemical: Chemical) => {
        setChemicalToArchive(chemical);
        setArchiveModalOpen(true);
    };

    const handleArchiveConfirm = async () => {
        if (!chemicalToArchive || !onArchive) return;
        setArchiveLoading(true);
        try {
            await onArchive(chemicalToArchive.id);
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.delete(chemicalToArchive.id);
                return next;
            });
        } finally {
            setArchiveLoading(false);
            setArchiveModalOpen(false);
            setChemicalToArchive(null);
        }
    };

    const handleUploadSdsClick = () => {
        const selected = chemicals.filter((c) => selectedIds.has(c.id));
        if (selected.length === 0) {
            toast('Select chemicals to upload SDS');
            return;
        }
        setSdsDialogOpen(true);
    };

    const handleSdsUpload = async (chemicalId: string, file: File) => {
        await onUploadSds(chemicalId, file);
    };

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
            <div className="max-w-[1500px] mx-auto w-full">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-medium">Hazardous Chemical Register</h1>
                    <p className="text-sm text-muted-foreground mt-1">GHS-compliant register of workplace hazardous substances</p>
                </div>
                <div className="flex items-center gap-2">
                    {activeTab === 'active' && (
                        <>
                            <button onClick={handleUploadSdsClick} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                                <Upload size={16} />
                                Upload SDS
                            </button>
                            <button onClick={handleExport} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                                <Download size={16} />
                                Export Register
                            </button>
                            {onAdd && (
                                <button onClick={onAdd} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                                    <Plus size={16} />
                                    Add Chemical
                                </button>
                            )}
                        </>
                    )}
                    {activeTab === 'archived' && (
                        <button onClick={handleExport} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                            <Download size={16} />
                            Export Archived
                        </button>
                    )}
                </div>
            </div>

            {/* Active / Archived Tabs — matching People page style */}
            <Tabs value={activeTab} onValueChange={(v) => onTabChange(v as 'active' | 'archived')} className="w-full mb-4">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger
                        value="active"
                        className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white"
                    >
                        Active
                    </TabsTrigger>
                    <TabsTrigger
                        value="archived"
                        className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white"
                    >
                        Archived
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <select
                        value={searchColumn}
                        onChange={(e) => onSearchColumn(e.target.value)}
                        className="h-10 px-3 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                        <option value="name">Chemical Name</option>
                        <option value="quantity">Quantity</option>
                        <option value="unNumber">UN Number</option>
                        <option value="poisonSchedule">Poison / pH</option>
                        <option value="storage">Storage</option>
                        <option value="intendedUse">Intended Use</option>
                        <option value="location">Location</option>
                    </select>
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={search}
                            onChange={(e) => onSearch(e.target.value)}
                            placeholder={`Search ${searchColumn === 'name' ? 'chemical name' : searchColumn === 'unNumber' ? 'UN number' : searchColumn === 'poisonSchedule' ? 'poison / pH' : searchColumn}...`}
                            className="w-64 h-10 pl-9 pr-3 rounded-lg border border-border bg-card text-sm"
                        />
                    </div>
                    {search && (
                        <button
                            onClick={() => onSearch('')}
                            className="h-10 px-3 rounded-lg border border-border bg-card text-sm text-muted-foreground hover:text-foreground hover:bg-accent"
                        >
                            Clear
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {selectedIds.size > 0 && (
                        <span className="text-xs text-muted-foreground">
                            {selectedIds.size} selected
                        </span>
                    )}
                    {activeTab === 'active' && missingSds > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive bg-destructive/5 border border-destructive/30 px-3 py-1.5 rounded-full">
                            <TriangleAlert size={13} />
                            {missingSds} missing SDS
                        </span>
                    )}
                    {activeTab === 'active' && expiredReviews > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-300 px-3 py-1.5 rounded-full">
                            <TriangleAlert size={13} />
                            {expiredReviews} expired reviews
                        </span>
                    )}
                </div>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead className="border-b border-border">
                            <tr>
                                <th className="px-3 py-3 w-10">
                                    <input
                                        type="checkbox"
                                        className="rounded border-border"
                                        checked={allSelected}
                                        ref={(el) => {
                                            if (el) el.indeterminate = someSelected;
                                        }}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                {COLS.map((c) => (
                                    <th key={c} className="text-left font-medium text-xs uppercase tracking-wide text-muted-foreground px-3 py-3 whitespace-nowrap">
                                        {c}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {chemicals.length === 0 && (
                                <tr>
                                    <td colSpan={13} className="px-3 py-16 text-center text-sm text-muted-foreground">
                                        No chemicals in the register.
                                    </td>
                                </tr>
                            )}
                            {chemicals.map((c) => {
                                const reviewDue = isReviewDue(c.lastReview);
                                const sdsMissing = sdsStatusOf(c) === 'Missing';
                                const isSelected = selectedIds.has(c.id);
                                return (
                                    <tr
                                        key={c.id}
                                        onClick={() => navigate(`/safety/hazardous-chemicals/${c.id}`)}
                                        className={`border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer text-sm ${isSelected ? 'bg-accent/20' : ''}`}
                                    >
                                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                className="rounded border-border"
                                                checked={isSelected}
                                                onChange={() => toggleSelect(c.id)}
                                            />
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <FlaskConical size={15} className={c.ghsSignal === 'Danger' ? 'text-destructive' : 'text-muted-foreground'} />
                                                <span className="font-medium">{c.name}</span>
                                                {c.dangerousGoods && <DgTag />}
                                                {sdsMissing && <NoSdsTag />}
                                                {reviewDue && <ReviewDueTag />}
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{c.quantity}</td>
                                        <td className="px-3 py-3 whitespace-nowrap"><GhsPill signal={c.ghsSignal} /></td>
                                        <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{c.unNumber || 'N/A'}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{c.poisonSchedule || 'N/A'}</td>
                                        <td className="px-3 py-3 max-w-[160px] truncate text-muted-foreground">{c.storage}</td>
                                        <td className="px-3 py-3 max-w-[140px] truncate text-muted-foreground">{c.intendedUse}</td>
                                        <td className="px-3 py-3 max-w-[160px] truncate text-muted-foreground">{c.ppeRequired}</td>
                                        <td className="px-3 py-3 whitespace-nowrap">
                                            {c.sds ? (
                                                <span className="text-xs font-medium text-emerald-600">✓ Attached</span>
                                            ) : (
                                                <span className="text-xs font-medium text-destructive">Missing</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{c.location}</td>
                                        <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{c.owner}</td>
                                        <td className="px-3 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => navigate(`/safety/hazardous-chemicals/${c.id}`)}
                                                    className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                                                    title="View"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                {activeTab === 'active' && onEdit && (
                                                    <button
                                                        onClick={() => onEdit(c)}
                                                        className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                )}
                                                {activeTab === 'active' && onArchive && (
                                                    <button
                                                        onClick={() => openArchiveModal(c)}
                                                        className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                                        title="Archive"
                                                    >
                                                        <Archive size={16} />
                                                    </button>
                                                )}
                                                {activeTab === 'archived' && onRestore && (
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await onRestore(c.id);
                                                            } catch {
                                                                // error toast handled in parent
                                                            }
                                                        }}
                                                        className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                        title="Restore"
                                                    >
                                                        <RefreshCw size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            </div>

            {/* Archive Confirmation Modal */}
            <AlertDialog open={archiveModalOpen && !!chemicalToArchive} onOpenChange={(open) => { if (!open) { setArchiveModalOpen(false); setChemicalToArchive(null); } }}>
                <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
                    <AlertDialogHeader className="p-6 pb-4">
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={20} className="text-red-500" />
                            <AlertDialogTitle className="text-lg font-bold text-gray-900">Archive Chemical</AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
                            Are you sure you want to archive <span className="font-bold text-gray-700">"{chemicalToArchive?.name}"</span>? This will move it to the Archived tab. It can be restored at any time for WHS record-keeping.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="px-6 pb-6 gap-2">
                        <AlertDialogCancel
                            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                            disabled={archiveLoading}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <Button
                            className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
                            onClick={handleArchiveConfirm}
                            disabled={archiveLoading}
                        >
                            {archiveLoading ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                'Archive Chemical'
                            )}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Bulk SDS Upload Dialog */}
            <SdsUploadDialog
                open={sdsDialogOpen}
                chemicals={chemicals.filter((c) => selectedIds.has(c.id))}
                onClose={() => setSdsDialogOpen(false)}
                onUpload={handleSdsUpload}
            />
        </div>
    );
};
